import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { 
  X, 
  Type, 
  Image, 
  Square, 
  Columns, 
  Link, 
  Code, 
  Save,
  Eye,
  Smartphone,
  Monitor,
  Bold,
  Italic,
  Underline,
  AlignLeft,
  AlignCenter,
  AlignRight,
  List,
  ListOrdered,
  ImageIcon,
  Trash2,
  Copy,
  Move,
  Settings,
  ChevronDown,
  ChevronUp,
  Plus,
  Palette,
  Layout,
  Mail,
  Calendar,
  Gift,
  Star,
  Heart,
  ShoppingBag,
  Megaphone,
  FileText,
  Users,
  Images,
  Upload,
  GripVertical
} from 'lucide-react';
import { useBusiness } from '../contexts/BusinessContext';
import { supabase } from '../lib/supabase';
import StockPhotoBrowser from './StockPhotoBrowser';
// Use Supabase Storage for better performance
import { uploadedImageService } from '../services/uploadedImageService';
// Fallback: Use local storage if bucket not available
// import { uploadedImageService } from '../services/uploadedImageServiceLocal';
import { useAuth } from '../hooks/useAuth';

interface EmailTemplateBuilderProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (template: any) => void;
  initialTemplate?: any;
}

interface UploadedImage {
  id: string;
  url: string;
  name: string;
  uploadedAt: Date;
}

// Pre-designed templates
const emailTemplates = [
  {
    id: 'welcome',
    name: 'Welcome Email',
    icon: Heart,
    category: 'Onboarding',
    preview: 'A warm welcome message for new customers',
    content: {
      subject: 'Welcome to {{business_name}}!',
      preheader: 'We\'re excited to have you with us',
      sections: [
        {
          type: 'header',
          content: {
            displayMode: 'logo',
            logoColorMode: 'white',
            logoSize: 80,
            backgroundColor: '#6366f1',
            textColor: '#ffffff',
            padding: '40px'
          }
        },
        {
          type: 'hero',
          content: {
            heading: 'Welcome Aboard!',
            subheading: 'We\'re thrilled to have you join our community',
            image: 'https://images.unsplash.com/photo-1556761175-b413da4baf72?w=800',
            buttonText: 'Get Started',
            buttonUrl: ''
          }
        },
        {
          type: 'text',
          content: {
            text: 'Thank you for choosing {{business_name}}. We\'re committed to providing you with exceptional service.',
            align: 'center'
          }
        }
      ]
    }
  },
  {
    id: 'booking-confirmation',
    name: 'Booking Confirmation',
    icon: Calendar,
    category: 'Bookings',
    preview: 'Confirm customer bookings with style',
    content: {
      subject: 'Booking Confirmed - {{booking_date}}',
      preheader: 'Your booking has been confirmed',
      sections: [
        {
          type: 'header',
          content: {
            displayMode: 'logo',
            logoColorMode: 'white',
            logoSize: 80,
            backgroundColor: '#10b981',
            textColor: '#ffffff',
            padding: '30px'
          }
        },
        {
          type: 'text',
          content: {
            text: 'Hi {{customer_name}},\n\nYour booking has been confirmed!',
            align: 'left'
          }
        },
        {
          type: 'card',
          content: {
            title: 'Booking Details',
            items: [
              'Date: {{booking_date}}',
              'Time: {{booking_time}}',
              'Service: {{service_name}}',
              'Location: {{location}}'
            ]
          }
        }
      ]
    }
  },
  {
    id: 'promotion',
    name: 'Promotional Offer',
    icon: Gift,
    category: 'Marketing',
    preview: 'Eye-catching promotional email',
    content: {
      subject: 'Special Offer - {{discount}}% Off!',
      preheader: 'Limited time offer just for you',
      sections: [
        {
          type: 'header',
          content: {
            displayMode: 'logo',
            logoColorMode: 'white',
            logoSize: 80,
            backgroundColor: '#ef4444',
            textColor: '#ffffff',
            padding: '30px'
          }
        },
        {
          type: 'hero',
          content: {
            heading: '{{discount}}% OFF',
            subheading: 'Limited Time Offer',
            buttonText: 'Shop Now',
            buttonUrl: ''
          }
        }
      ]
    }
  },
  {
    id: 'reminder',
    name: 'Service Reminder',
    icon: Calendar,
    category: 'Reminders',
    preview: 'Gentle reminder for upcoming services',
    content: {
      subject: 'Reminder: Your appointment is tomorrow',
      preheader: 'Don\'t forget your upcoming appointment',
      sections: [
        {
          type: 'header',
          content: {
            displayMode: 'logo',
            logoColorMode: 'white',
            logoSize: 80,
            backgroundColor: '#f59e0b',
            textColor: '#ffffff',
            padding: '30px'
          }
        },
        {
          type: 'text',
          content: {
            text: 'Hi {{customer_name}},\n\nThis is a friendly reminder about your upcoming appointment.',
            align: 'left'
          }
        },
        {
          type: 'card',
          content: {
            title: 'Appointment Details',
            items: [
              'Date: {{appointment_date}}',
              'Time: {{appointment_time}}',
              'Service: {{service_name}}'
            ]
          }
        },
        {
          type: 'button',
          content: {
            text: 'View Appointment',
            url: '',
            backgroundColor: '#f59e0b',
            align: 'center'
          }
        }
      ]
    }
  },
  {
    id: 'feedback',
    name: 'Feedback Request',
    icon: Star,
    category: 'Feedback',
    preview: 'Request customer feedback and reviews',
    content: {
      subject: 'How was your experience?',
      preheader: 'We\'d love to hear from you',
      sections: [
        {
          type: 'header',
          content: {
            displayMode: 'logo',
            logoColorMode: 'white',
            logoSize: 80,
            backgroundColor: '#8b5cf6',
            textColor: '#ffffff',
            padding: '30px'
          }
        },
        {
          type: 'hero',
          content: {
            heading: 'How Did We Do?',
            subheading: 'Your feedback helps us improve',
            buttonText: 'Leave a Review',
            buttonUrl: ''
          }
        },
        {
          type: 'text',
          content: {
            text: 'Thank you for choosing {{business_name}}. We\'d love to hear about your recent experience with us.',
            align: 'center'
          }
        }
      ]
    }
  },
  {
    id: 'newsletter',
    name: 'Newsletter',
    icon: FileText,
    category: 'Updates',
    preview: 'Monthly newsletter template',
    content: {
      subject: 'Monthly Newsletter - {{month}}',
      preheader: 'Latest news and updates',
      sections: [
        {
          type: 'header',
          content: {
            displayMode: 'logo',
            logoColorMode: 'white',
            logoSize: 80,
            backgroundColor: '#0891b2',
            textColor: '#ffffff',
            padding: '30px'
          }
        },
        {
          type: 'hero',
          content: {
            heading: 'Monthly Update',
            subheading: 'Stay informed with our latest news',
            image: 'https://images.unsplash.com/photo-1504868584819-f8e8b4b6d7e3?w=800'
          }
        },
        {
          type: 'text',
          content: {
            text: 'Check out what\'s new this month at {{business_name}}',
            align: 'center'
          }
        }
      ]
    }
  },
  {
    id: 'invoice',
    name: 'Invoice',
    icon: FileText,
    category: 'Billing',
    preview: 'Professional invoice template',
    content: {
      subject: 'Invoice #{{invoice_number}}',
      preheader: 'Payment details for your recent service',
      sections: [
        {
          type: 'header',
          content: {
            displayMode: 'logo',
            logoColorMode: 'white',
            logoSize: 80,
            backgroundColor: '#1f2937',
            textColor: '#ffffff',
            padding: '30px'
          }
        },
        {
          type: 'text',
          content: {
            text: 'Invoice #{{invoice_number}}',
            align: 'center',
            fontSize: '24px'
          }
        },
        {
          type: 'card',
          content: {
            title: 'Invoice Details',
            items: [
              'Date: {{invoice_date}}',
              'Due Date: {{due_date}}',
              'Amount: {{total_amount}}'
            ]
          }
        },
        {
          type: 'button',
          content: {
            text: 'Pay Now',
            url: '',
            backgroundColor: '#059669',
            align: 'center'
          }
        }
      ]
    }
  },
  {
    id: 'thank-you',
    name: 'Thank You',
    icon: Heart,
    category: 'Customer Care',
    preview: 'Express gratitude to your customers',
    content: {
      subject: 'Thank You for Your Business!',
      preheader: 'We appreciate you',
      sections: [
        {
          type: 'header',
          content: {
            displayMode: 'logo',
            logoColorMode: 'white',
            logoSize: 80,
            backgroundColor: '#ec4899',
            textColor: '#ffffff',
            padding: '30px'
          }
        },
        {
          type: 'hero',
          content: {
            heading: 'Thank You!',
            subheading: 'We truly appreciate your business',
            backgroundColor: '#fef2f2'
          }
        },
        {
          type: 'text',
          content: {
            text: 'Dear {{customer_name}},\n\nThank you for choosing {{business_name}}. Your trust in us means everything.',
            align: 'center'
          }
        }
      ]
    }
  },
  {
    id: 'event',
    name: 'Event Invitation',
    icon: Megaphone,
    category: 'Events',
    preview: 'Invite customers to special events',
    content: {
      subject: 'You\'re Invited: {{event_name}}',
      preheader: 'Join us for a special event',
      sections: [
        {
          type: 'header',
          content: {
            displayMode: 'logo',
            logoColorMode: 'white',
            logoSize: 80,
            backgroundColor: '#7c3aed',
            textColor: '#ffffff',
            padding: '30px'
          }
        },
        {
          type: 'hero',
          content: {
            heading: 'You\'re Invited!',
            subheading: '{{event_name}}',
            image: 'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=800',
            buttonText: 'RSVP Now',
            buttonUrl: ''
          }
        },
        {
          type: 'card',
          content: {
            title: 'Event Details',
            items: [
              'Date: {{event_date}}',
              'Time: {{event_time}}',
              'Location: {{event_location}}'
            ]
          }
        }
      ]
    }
  },
  {
    id: 'loyalty',
    name: 'Loyalty Program',
    icon: Star,
    category: 'Rewards',
    preview: 'Loyalty program updates and rewards',
    content: {
      subject: 'You\'ve Earned Rewards!',
      preheader: 'Check out your loyalty rewards',
      sections: [
        {
          type: 'header',
          content: {
            displayMode: 'logo',
            logoColorMode: 'white',
            logoSize: 80,
            backgroundColor: '#eab308',
            textColor: '#ffffff',
            padding: '30px'
          }
        },
        {
          type: 'hero',
          content: {
            heading: '🎉 Rewards Unlocked!',
            subheading: 'You\'ve earned {{points}} points',
            buttonText: 'Claim Rewards',
            buttonUrl: '',
            backgroundColor: '#fef3c7'
          }
        },
        {
          type: 'text',
          content: {
            text: 'Thank you for being a valued member of our loyalty program!',
            align: 'center'
          }
        }
      ]
    }
  }
];

// Template selection modal
function TemplateSelector({ onSelect, onClose }: { onSelect: (template: any) => void; onClose: () => void }) {
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);
  const [hoveredTemplate, setHoveredTemplate] = useState<string | null>(null);
  const { selectedBusiness } = useBusiness();
  const logoUrl = selectedBusiness?.booking_settings?.logo_url;

  const renderTemplatePreview = (template: any) => {
    if (!template || !template.content || !template.content.sections) return null;
    
    return (
      <div className="w-full bg-white rounded-lg overflow-hidden shadow-sm">
        {template.content.sections.map((section: any, index: number) => {
          switch (section.type) {
            case 'header': {
              const thumbDisplayMode = section.content.displayMode || (section.content.logo ? 'logo' : 'name');
              const thumbLogoSize = Math.min((section.content.logoSize || 80) * 0.4, 48);
              const thumbLogoFilter = section.content.logoColorMode === 'white'
                ? 'brightness(0) invert(1)'
                : section.content.logoColorMode === 'black'
                  ? 'brightness(0)'
                  : section.content.logoInvert ? 'brightness(0) invert(1)' : 'none';
              return (
                <div
                  key={index}
                  style={{ 
                    backgroundColor: section.content.backgroundColor || '#6366f1', 
                    padding: '20px'
                  }}
                  className="text-center"
                >
                  {thumbDisplayMode === 'logo' && logoUrl ? (
                    <img 
                      src={logoUrl} 
                      alt={selectedBusiness?.name || 'Business Logo'} 
                      className="mx-auto object-contain"
                      style={{ height: `${thumbLogoSize}px`, filter: thumbLogoFilter }}
                    />
                  ) : (
                    <div className="text-white font-bold text-lg">{selectedBusiness?.name || 'Your Logo'}</div>
                  )}
                </div>
              );
            }
            case 'hero':
              return (
                <div key={index} style={{ backgroundColor: section.content.backgroundColor || '#f3f4f6' }} className="p-6 text-center">
                  {section.content.image && (
                    <div className="w-full h-32 bg-gray-200 rounded mb-3 flex items-center justify-center">
                      <ImageIcon className="h-8 w-8 text-gray-400" />
                    </div>
                  )}
                  {section.content.heading && <h2 className="text-xl font-bold mb-1" dangerouslySetInnerHTML={{ __html: section.content.heading }} />}
                  {section.content.subheading && <p className="text-sm text-gray-600 mb-3" dangerouslySetInnerHTML={{ __html: section.content.subheading }} />}
                  {section.content.buttonText && (
                    <button
                      className="px-4 py-2 text-white rounded text-sm"
                      style={{
                        backgroundColor: section.content.buttonBackgroundColor || '#6366f1',
                        color: section.content.buttonColor || '#ffffff',
                      }}
                      dangerouslySetInnerHTML={{ __html: section.content.buttonText }}
                    />
                  )}
                </div>
              );
            case 'text':
              return (
                <div key={index} className="p-4" style={{ textAlign: section.content.align || 'left' }}>
                  {section.content.text
                    ? <div className="text-sm text-gray-700 line-clamp-2" style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6' }} dangerouslySetInnerHTML={{ __html: section.content.text }} />
                    : null
                  }
                </div>
              );
            case 'card':
              return (
                <div key={index} className="p-4">
                  <div className="p-3 bg-gray-50 rounded border border-gray-200">
                    {section.content.title
                      ? <h4 className="font-semibold text-sm mb-1" dangerouslySetInnerHTML={{ __html: section.content.title }} />
                      : null
                    }
                    {section.content.items ? (
                      <ul className="text-xs text-gray-600 space-y-1">
                        {section.content.items.slice(0, 2).map((item: string, i: number) => (
                          <li key={i}>• {item}</li>
                        ))}
                      </ul>
                    ) : section.content.content ? (
                      <p className="text-xs text-gray-600" dangerouslySetInnerHTML={{ __html: section.content.content }} />
                    ) : null}
                  </div>
                </div>
              );
            case 'button':
              return (
                <div key={index} className="p-4" style={{ textAlign: section.content.align || 'center' }}>
                  {section.content.text
                    ? <button style={{ backgroundColor: section.content.backgroundColor || '#6366f1', color: section.content.color || '#ffffff' }} className="px-4 py-2 rounded text-sm font-medium" dangerouslySetInnerHTML={{ __html: section.content.text }} />
                    : <button style={{ backgroundColor: section.content.backgroundColor || '#6366f1', color: section.content.color || '#ffffff' }} className="px-4 py-2 rounded text-sm font-medium">Button</button>
                  }
                </div>
              );
            case 'footer':
              return (
                <div key={index} style={{ backgroundColor: section.content.backgroundColor || '#f9fafb', color: section.content.color || '#6b7280' }} className="p-4 text-center text-xs">
                  {section.content.text
                    ? <span dangerouslySetInnerHTML={{ __html: section.content.text }} />
                    : <span>Footer</span>
                  }
                </div>
              );
            case 'divider':
              return (
                <div key={index} style={{ padding: section.content.margin || '10px 20px' }}>
                  <hr style={{ borderColor: section.content.color || '#e5e7eb', borderWidth: section.content.thickness || '1px' }} />
                </div>
              );
            case 'image':
              return section.content.url ? (
                <div key={index} className="p-4" style={{ textAlign: section.content.align || 'center' }}>
                  <img src={section.content.url} alt={section.content.alt || ''} className="mx-auto rounded" style={{ width: section.content.width || '100%', maxHeight: '120px', objectFit: 'cover' }} />
                </div>
              ) : null;
            case 'spacer':
              return <div key={index} style={{ height: (section.content.height || 20) * 0.5 }} />;
            default:
              return null;
          }
        })}
      </div>
    );
  };

  return (
    <div 
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex flex-col sm:items-center sm:justify-center z-[100010]"
      style={{
        paddingTop: 'env(safe-area-inset-top, 0px)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      }}
    >
      <div className="bg-white sm:rounded-2xl w-full sm:max-w-7xl h-full sm:h-auto sm:max-h-[90vh] overflow-hidden flex flex-col sm:mx-4">
        <div className="px-4 sm:px-6 py-4 sm:py-5 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Choose a Template</h2>
              <p className="text-gray-500 mt-0.5 text-sm sm:text-base">Select a template or start from scratch</p>
            </div>
            <button
              onClick={onClose}
              className="p-2.5 hover:bg-gray-100 active:bg-gray-200 rounded-xl transition-colors touch-manipulation flex-shrink-0 ml-3"
              style={{ minWidth: '44px', minHeight: '44px' }}
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 sm:p-6" style={{ WebkitOverflowScrolling: 'touch' }}>
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-6">
            {/* Blank Template Option */}
            <div
              onClick={() => onSelect(null)}
              onMouseEnter={() => setHoveredTemplate('blank')}
              onMouseLeave={() => setHoveredTemplate(null)}
              className="cursor-pointer touch-manipulation"
            >
              <div className="group bg-white border-2 border-dashed border-gray-300 rounded-2xl overflow-hidden hover:border-blue-500 active:border-blue-500 active:bg-blue-50 transition-colors h-[200px] sm:h-[400px] flex flex-col">
                <div className="flex-1 flex flex-col items-center justify-center p-4 sm:p-6">
                  <div className="w-14 h-14 sm:w-20 sm:h-20 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full flex items-center justify-center mb-3 sm:mb-4">
                    <Plus className="h-7 w-7 sm:h-10 sm:w-10 text-blue-600" />
                  </div>
                  <h3 className="text-sm sm:text-lg font-bold text-gray-900 mb-1 sm:mb-2">Start Blank</h3>
                  <p className="text-xs sm:text-sm text-gray-600 text-center">Create from scratch</p>
                </div>
              </div>
            </div>

            {/* Pre-designed Templates */}
            {emailTemplates.map((template) => {
              const Icon = template.icon;
              
              return (
                <div
                  key={template.id}
                  onClick={() => onSelect(template)}
                  onMouseEnter={() => setHoveredTemplate(template.id)}
                  onMouseLeave={() => setHoveredTemplate(null)}
                  className="cursor-pointer touch-manipulation"
                >
                  <div className="group bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-xl active:bg-gray-50 transition-colors h-[200px] sm:h-[400px] flex flex-col">
                    {/* Template Preview */}
                    <div className="flex-1 bg-gray-50 p-2 sm:p-3 overflow-hidden">
                      <div className="transform scale-50 sm:scale-75 origin-top">
                        {renderTemplatePreview(template)}
                      </div>
                    </div>
                    
                    {/* Template Info */}
                    <div className="p-2.5 sm:p-4 bg-white border-t border-gray-100">
                      <div className="flex items-start space-x-2 sm:space-x-3">
                        <div className="hidden sm:flex w-10 h-10 bg-gradient-to-br from-blue-100 to-blue-200 rounded-xl items-center justify-center flex-shrink-0">
                          <Icon className="h-5 w-5 text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-xs sm:text-sm font-bold text-gray-900 truncate">{template.name}</h3>
                          <p className="text-[10px] sm:text-xs text-gray-600 line-clamp-1 sm:line-clamp-2 mt-0.5 sm:mt-1">{template.preview}</p>
                          <span className="inline-flex items-center px-1.5 sm:px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium bg-blue-100 text-blue-800 mt-1 sm:mt-2">
                            {template.category}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// Main Email Template Builder
export default function EmailTemplateBuilder({ isOpen, onClose, onSave, initialTemplate }: EmailTemplateBuilderProps) {
  // Show template selector when creating new (no initialTemplate) and hide when editing (has initialTemplate)
  const [showTemplateSelector, setShowTemplateSelector] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);
  const [templateName, setTemplateName] = useState<string>('');
  const [emailContent, setEmailContent] = useState<any>({
    subject: '',
    preheader: '',
    sections: []
  });
  const [selectedSection, setSelectedSection] = useState<number | null>(null);
  const [previewMode, setPreviewMode] = useState<'desktop' | 'mobile'>('desktop');
  const [showStockPhotos, setShowStockPhotos] = useState(false);
  const [stockPhotoTarget, setStockPhotoTarget] = useState<{ sectionIndex: number; field: string } | null>(null);
  const [uploadedImages, setUploadedImages] = useState<UploadedImage[]>([]);
  const [isUploadingImages, setIsUploadingImages] = useState(false);
  // Mobile-specific states
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const { selectedBusiness } = useBusiness();
  const { user, currentResourceId } = useAuth();

  // Dirty state tracking
  const savedSnapshotRef = useRef<string>('');
  const isNewTemplateRef = useRef<boolean>(false);
  const savedOneOffRef = useRef(false);
  const [oneOff, setOneOff] = useState(false);
  const getCurrentSnapshot = useCallback(() => {
    return JSON.stringify({ name: templateName, content: emailContent });
  }, [templateName, emailContent]);
  const hasUnsavedChanges = isNewTemplateRef.current || oneOff !== savedOneOffRef.current || (savedSnapshotRef.current !== '' && getCurrentSnapshot() !== savedSnapshotRef.current);

  // Track whether the editor was opened via AI loading
  const wasAILoadingRef = useRef(false);

  // Initialize based on whether we're creating or editing — only on open transition
  const prevIsOpenRef = useRef(false);
  useEffect(() => {
    if (isOpen && !prevIsOpenRef.current) {
      // Modal just opened
      setOneOff(!!initialTemplate?.one_off);
      savedOneOffRef.current = !!initialTemplate?.one_off;
      if (initialTemplate?.content) {
        setEmailContent(initialTemplate.content);
        setSelectedTemplate(initialTemplate);
        setTemplateName(initialTemplate.name || '');
        setShowTemplateSelector(false);
        wasAILoadingRef.current = !!initialTemplate._aiLoading;
        if (!initialTemplate._aiLoading) {
          savedSnapshotRef.current = JSON.stringify({ name: initialTemplate.name || '', content: initialTemplate.content });
          isNewTemplateRef.current = !initialTemplate.id;
        } else {
          isNewTemplateRef.current = true;
        }
      } else {
        setShowTemplateSelector(true);
        setSelectedTemplate(null);
        setEmailContent({
          subject: '',
          preheader: '',
          sections: []
        });
        setTemplateName('');
        savedSnapshotRef.current = '';
        isNewTemplateRef.current = true;
        wasAILoadingRef.current = false;
      }
      
      if (selectedBusiness?.id) {
        loadSavedImages();
      }
    }

    // Handle AI template arriving while builder is already open
    if (isOpen && prevIsOpenRef.current && wasAILoadingRef.current && initialTemplate?.content && !initialTemplate._aiLoading) {
      setEmailContent(initialTemplate.content);
      setSelectedTemplate(initialTemplate);
      setTemplateName(initialTemplate.name || '');
      setShowTemplateSelector(false);
      wasAILoadingRef.current = false;
      savedSnapshotRef.current = '';
      isNewTemplateRef.current = true;
    }

    if (!isOpen) {
      savedSnapshotRef.current = '';
      isNewTemplateRef.current = false;
      wasAILoadingRef.current = false;
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen, initialTemplate, selectedBusiness?.id]);

  // Load saved images from database
  const loadSavedImages = async () => {
    if (!selectedBusiness?.id) return;
    
    try {
      const images = await uploadedImageService.getBusinessImages(selectedBusiness.id);
      // Convert to the format expected by the component
      const formattedImages = images.map(img => ({
        id: img.id,
        url: img.url,
        name: img.name,
        uploadedAt: new Date(img.created_at)
      }));
      setUploadedImages(formattedImages);
    } catch (error) {
      console.error('Error loading saved images:', error);
    }
  };

  // Handle template selection from selector
  useEffect(() => {
    if (selectedTemplate?.content && !initialTemplate) {
      setEmailContent(selectedTemplate.content);
      setTemplateName(selectedTemplate.name || '');
      // Capture snapshot after template selection for dirty tracking
      savedSnapshotRef.current = JSON.stringify({ name: selectedTemplate.name || '', content: selectedTemplate.content });
    }
  }, [selectedTemplate]);

  // Section types available to add
  const sectionTypes = [
    { type: 'header', icon: Layout, label: 'Header' },
    { type: 'hero', icon: Image, label: 'Hero Section' },
    { type: 'text', icon: Type, label: 'Text Block' },
    { type: 'image', icon: ImageIcon, label: 'Image' },
    { type: 'button', icon: Square, label: 'Button' },
    { type: 'divider', icon: Columns, label: 'Divider' },
    { type: 'card', icon: Square, label: 'Card' },
    { type: 'footer', icon: Layout, label: 'Footer' },
  ];

  // Grouped palette (mirrors the docs builder's grouped block palette).
  const sectionGroups: { label: string; types: string[] }[] = [
    { label: 'Layout', types: ['header', 'hero', 'footer'] },
    { label: 'Content', types: ['text', 'image', 'card'] },
    { label: 'Actions', types: ['button'] },
    { label: 'Structure', types: ['divider'] },
  ];
  const sectionMetaByType: Record<string, { icon: any; label: string }> = sectionTypes.reduce(
    (acc, s) => { acc[s.type] = { icon: s.icon, label: s.label }; return acc; },
    {} as Record<string, { icon: any; label: string }>,
  );
  const sectionIcon = (type: string) => sectionMetaByType[type]?.icon || Square;
  const sectionLabel = (type: string) => sectionMetaByType[type]?.label || type;

  const addSection = (type: string) => {
    const newSection = {
      type,
      content: getDefaultSectionContent(type)
    };
    setEmailContent({
      ...emailContent,
      sections: [...emailContent.sections, newSection]
    });
  };

  const handleImageUpload = async () => {
    if (!selectedBusiness?.id) {
      console.error('No business selected');
      return;
    }

    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.multiple = true;
    input.onchange = async (e: any) => {
      const files = Array.from(e.target.files || []) as File[];
      if (files.length === 0) return;

      setIsUploadingImages(true);
      
      try {
        // Upload images to Supabase Storage and save to database
        const uploadedImgs = await uploadedImageService.uploadMultipleImages(
          files,
          selectedBusiness.id,
          currentResourceId
        );

        if (uploadedImgs.length > 0) {
          // Add to local state for immediate display
          const formattedImages = uploadedImgs.map(img => ({
            id: img.id,
            url: img.url,
            name: img.name,
            uploadedAt: new Date(img.created_at)
          }));
          
          setUploadedImages(prev => [...prev, ...formattedImages]);
          console.log(`Successfully uploaded ${uploadedImgs.length} images`);
        } else {
          console.error('No images were uploaded successfully');
        }
      } catch (error: any) {
        console.error('Error uploading images:', error);
        alert(`Failed to upload images: ${error.message || 'Unknown error'}`);
      } finally {
        setIsUploadingImages(false);
      }
    };
    input.click();
  };

  const getDefaultSectionContent = (type: string) => {
    switch (type) {
      case 'header':
        return {
          displayMode: 'logo',
          showBusinessName: true,
          logoColorMode: 'original',
          logoSize: 80,
          backgroundColor: '#6366f1',
          textColor: '#ffffff',
          padding: '30px'
        };
      case 'hero':
        return {
          heading: 'Your Heading Here',
          subheading: 'Your subheading text',
          image: '',
          buttonText: 'Call to Action',
          buttonUrl: '',
          buttonBackgroundColor: '#6366f1',
          buttonColor: '#ffffff',
          backgroundColor: '#f3f4f6'
        };
      case 'text':
        return {
          text: 'Enter your text content here...',
          align: 'left',
          fontSize: '16px',
          color: '#374151'
        };
      case 'image':
        return {
          url: '',
          alt: 'Image description',
          width: '100%',
          align: 'center'
        };
      case 'button':
        return {
          text: 'Button Text',
          url: '',
          backgroundColor: '#6366f1',
          color: '#ffffff',
          align: 'center'
        };
      case 'columns':
        return {
          columns: [
            { content: 'Column 1 content' },
            { content: 'Column 2 content' }
          ]
        };
      case 'divider':
        return {
          color: '#e5e7eb',
          thickness: '1px',
          margin: '20px'
        };
      case 'card':
        return {
          title: 'Card Title',
          content: 'Card content goes here',
          backgroundColor: '#f9fafb',
          borderColor: '#e5e7eb'
        };
      case 'footer':
        return {
          text: '© 2024 Your Business. All rights reserved.',
          links: [],
          backgroundColor: '#1f2937',
          color: '#ffffff'
        };
      default:
        return {};
    }
  };

  const updateSection = (index: number, content: any) => {
    const updatedSections = [...emailContent.sections];
    updatedSections[index].content = { ...updatedSections[index].content, ...content };
    setEmailContent({ ...emailContent, sections: updatedSections });
  };

  const deleteSection = (index: number) => {
    const updatedSections = emailContent.sections.filter((_, i) => i !== index);
    setEmailContent({ ...emailContent, sections: updatedSections });
    setSelectedSection(null);
  };

  const duplicateSection = (index: number) => {
    const sectionToDuplicate = emailContent.sections[index];
    const updatedSections = [...emailContent.sections];
    updatedSections.splice(index + 1, 0, { ...sectionToDuplicate });
    setEmailContent({ ...emailContent, sections: updatedSections });
  };

  const moveSection = (fromIndex: number, toIndex: number) => {
    const updatedSections = [...emailContent.sections];
    const [movedSection] = updatedSections.splice(fromIndex, 1);
    updatedSections.splice(toIndex, 0, movedSection);
    setEmailContent({ ...emailContent, sections: updatedSections });
    
    // Update selected section to follow the moved section
    setSelectedSection(toIndex);
  };

  const moveSectionUp = (index: number) => {
    if (index > 0) {
      moveSection(index, index - 1);
    }
  };

  const moveSectionDown = (index: number) => {
    if (index < emailContent.sections.length - 1) {
      moveSection(index, index + 1);
    }
  };

  const handleSave = () => {
    const template = {
      id: initialTemplate?.id,
      name: templateName || 'Untitled Template',
      content: emailContent,
      category: selectedTemplate?.category || initialTemplate?.category || 'Custom',
      status: selectedTemplate?.status || initialTemplate?.status || 'draft',
      one_off: oneOff,
      createdAt: initialTemplate?.createdAt || new Date().toISOString()
    };
    onSave(template);
    savedSnapshotRef.current = getCurrentSnapshot();
    isNewTemplateRef.current = false;
  };

  const handleCloseAttempt = () => {
    if (hasUnsavedChanges) {
      setShowUnsavedDialog(true);
    } else {
      onClose();
    }
  };

  const openPhotoBrowser = (sectionIndex: number, field: string) => {
    setStockPhotoTarget({ sectionIndex, field });
    setShowStockPhotos(true);
  };

  const handlePhotoSelect = (imageUrl: string) => {
    if (stockPhotoTarget) {
      const updatedSections = [...emailContent.sections];
      updatedSections[stockPhotoTarget.sectionIndex].content[stockPhotoTarget.field] = imageUrl;
      setEmailContent({ ...emailContent, sections: updatedSections });
      setStockPhotoTarget(null);
    }
  };

  const handleDeleteUploadedImage = async (imageId: string) => {
    try {
      // Delete from database and storage
      const success = await uploadedImageService.deleteImage(imageId);
      if (success) {
        // Remove from local state
        setUploadedImages(prev => prev.filter(img => img.id !== imageId));
      } else {
        console.error('Failed to delete image');
      }
    } catch (error) {
      console.error('Error deleting image:', error);
    }
  };

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedSection(null);
      // Don't reset template selector or uploaded images - they're handled elsewhere
    }
  }, [isOpen]);

  if (!isOpen) return null;

  if (showTemplateSelector) {
    return createPortal(
      <TemplateSelector
        onSelect={(template) => {
          setSelectedTemplate(template);
          setShowTemplateSelector(false);
        }}
        onClose={onClose}
      />,
      document.body
    );
  }

  return createPortal(
    <div 
      className="fixed inset-0 bg-gray-100 z-[100010] flex flex-col email-builder-root"
      style={{
        paddingTop: 'env(safe-area-inset-top, 0px)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      }}
    >
      <style>{`
        .email-builder-root .empty-editable:empty::before {
          content: attr(data-placeholder);
          color: #9ca3af;
          pointer-events: none;
        }
      `}</style>
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-3 sm:px-6 py-3 sm:py-4 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 sm:space-x-4 flex-1 min-w-0">
            <button
              onClick={handleCloseAttempt}
              className="p-2.5 hover:bg-gray-100 active:bg-gray-200 rounded-xl transition-colors flex-shrink-0 touch-manipulation"
              style={{ minWidth: '44px', minHeight: '44px' }}
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
            
            <div className="flex-1 min-w-0">
              <input
                type="text"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="Template Name"
                className="text-base sm:text-xl font-bold text-gray-900 bg-transparent border-none focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-lg px-2 py-1.5 w-full touch-manipulation"
                style={{ fontSize: '16px' }}
              />
              <div className="flex items-center gap-1 px-2 mt-0.5">
                <button
                  type="button"
                  onClick={() => setOneOff(false)}
                  className={`px-2 py-0.5 rounded-md text-[11px] font-medium ${!oneOff ? 'bg-blue-600 text-white' : 'text-gray-500 hover:bg-gray-100'}`}
                >
                  Saved template
                </button>
                <button
                  type="button"
                  onClick={() => setOneOff(true)}
                  className={`px-2 py-0.5 rounded-md text-[11px] font-medium ${oneOff ? 'bg-blue-600 text-white' : 'text-gray-500 hover:bg-gray-100'}`}
                >
                  One-off
                </button>
              </div>
            </div>
          </div>
          
          <div className="flex items-center space-x-2 sm:space-x-3 flex-shrink-0">
            {/* Preview Mode Toggle - Hidden on small screens */}
            <div className="hidden sm:flex items-center bg-gray-100 rounded-xl p-1">
              <button
                onClick={() => setPreviewMode('desktop')}
                className={`p-2 rounded-lg ${previewMode === 'desktop' ? 'bg-white shadow-sm' : ''}`}
              >
                <Monitor className="h-4 w-4 text-gray-600" />
              </button>
              <button
                onClick={() => setPreviewMode('mobile')}
                className={`p-2 rounded-lg ${previewMode === 'mobile' ? 'bg-white shadow-sm' : ''}`}
              >
                <Smartphone className="h-4 w-4 text-gray-600" />
              </button>
            </div>
            
            <button
              onClick={handleSave}
              disabled={!hasUnsavedChanges}
              className={`px-3.5 sm:px-4 py-2.5 rounded-xl transition-colors flex items-center space-x-1.5 sm:space-x-2 font-medium touch-manipulation ${
                hasUnsavedChanges
                  ? 'bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
              style={{ minHeight: '44px' }}
            >
              <Save className="h-4 w-4" />
              <span className="hidden sm:inline">Save Template</span>
              <span className="sm:hidden">Save</span>
            </button>
          </div>
        </div>
      </div>

      {/* Rich Text Formatting Toolbar */}
      {selectedSection !== null && ['text', 'hero', 'card', 'footer', 'button'].includes(emailContent.sections[selectedSection]?.type) && (
        <TextFormattingToolbar />
      )}

      <div className="flex-1 overflow-hidden flex">
        {/* Desktop grouped palette (docs-builder style) */}
        <div className="w-56 shrink-0 border-r border-gray-200 bg-white overflow-y-auto p-3 hidden md:block">
          {sectionGroups.map((group) => (
            <div key={group.label} className="mb-4">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1.5 px-1">{group.label}</div>
              <div className="space-y-1">
                {group.types.map((type) => {
                  const Icon = sectionIcon(type);
                  return (
                    <button
                      key={type}
                      onClick={() => addSection(type)}
                      className="w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-[13px] text-gray-700 hover:bg-blue-50 hover:text-blue-700 transition-colors text-left"
                    >
                      <Icon className="h-4 w-4 flex-shrink-0" /> {sectionLabel(type)}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Single-column canvas (docs-builder style) */}
        <div
          className="flex-1 overflow-y-auto bg-gray-50"
          style={{ WebkitOverflowScrolling: 'touch' }}
          onClick={() => setSelectedSection(null)}
        >
          <div
            className={`mx-auto p-4 sm:p-8 ${previewMode === 'mobile' ? 'max-w-sm' : 'max-w-2xl'}`}
            style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 6rem)' }}
          >
            {/* Meta card: subject + preheader */}
            <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3 mb-4" onClick={(e) => e.stopPropagation()}>
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1">Subject Line</label>
                <input
                  type="text"
                  value={emailContent.subject}
                  onChange={(e) => setEmailContent({ ...emailContent, subject: e.target.value })}
                  className="w-full text-sm text-gray-900 outline-none placeholder-gray-300"
                  style={{ fontSize: '16px' }}
                  placeholder="Enter email subject..."
                />
              </div>
              <div className="border-t border-gray-100 pt-3">
                <label className="block text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1">Preheader Text</label>
                <input
                  type="text"
                  value={emailContent.preheader}
                  onChange={(e) => setEmailContent({ ...emailContent, preheader: e.target.value })}
                  className="w-full text-sm text-gray-600 outline-none placeholder-gray-300"
                  style={{ fontSize: '16px' }}
                  placeholder="Preview text that appears in inbox..."
                />
              </div>
            </div>

            {/* Continuous email canvas — sections render flush so it reflects the real email */}
            <div className="bg-white shadow-lg rounded-xl sm:rounded-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
              {emailContent.sections.length === 0 ? (
                initialTemplate?._aiLoading ? (
                  <div className="p-12 sm:p-16 text-center flex flex-col items-center justify-center min-h-[300px]">
                    <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-5 animate-pulse">
                      <svg className="h-8 w-8 text-blue-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z" />
                        <path d="M5 3v4" /><path d="M19 17v4" /><path d="M3 5h4" /><path d="M17 19h4" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">AI is building your template...</h3>
                    <p className="text-sm text-gray-500 mb-6 max-w-xs">This usually takes a few seconds. Your template will appear here once it's ready.</p>
                    <div className="flex items-center gap-1.5">
                      <div className="w-2 h-2 rounded-full bg-blue-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                      <div className="w-2 h-2 rounded-full bg-blue-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                      <div className="w-2 h-2 rounded-full bg-blue-400 animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                ) : (
                  <div className="p-10 sm:p-14 text-center">
                    <div className="w-14 h-14 bg-blue-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <Plus className="h-7 w-7 text-blue-400" />
                    </div>
                    <p className="text-gray-600 text-sm">
                      <span className="hidden md:inline">Add sections from the left to build your email.</span>
                      <span className="md:hidden">Tap “Add section” to start building your email.</span>
                    </p>
                  </div>
                )
              ) : (
                emailContent.sections.map((section, index) => {
                  const isSel = selectedSection === index;
                  return (
                    <div
                      key={index}
                      className={`relative group ${isSel ? 'ring-2 ring-blue-500 ring-inset z-10' : ''}`}
                      onClick={(e) => { e.stopPropagation(); setSelectedSection(index); }}
                    >
                      {/* Floating controls — overlay so sections stay flush/continuous */}
                      <div
                        className={`absolute top-2 right-2 z-20 flex items-center gap-0.5 bg-white rounded-xl shadow-lg p-1 transition-opacity ${
                          isSel ? 'opacity-100' : 'opacity-0 pointer-events-none sm:group-hover:opacity-100 sm:group-hover:pointer-events-auto'
                        }`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          onClick={() => moveSectionUp(index)}
                          disabled={index === 0}
                          className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 disabled:opacity-30 disabled:hover:bg-transparent touch-manipulation"
                          title="Move up"
                        >
                          <ChevronUp className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => moveSectionDown(index)}
                          disabled={index === emailContent.sections.length - 1}
                          className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 disabled:opacity-30 disabled:hover:bg-transparent touch-manipulation"
                          title="Move down"
                        >
                          <ChevronDown className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => duplicateSection(index)}
                          className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 touch-manipulation"
                          title="Duplicate section"
                        >
                          <Copy className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => deleteSection(index)}
                          className="p-1.5 rounded-lg hover:bg-red-50 text-red-400 touch-manipulation"
                          title="Delete section"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>

                      {/* WYSIWYG section preview / inline edit (flush) */}
                      <SectionRenderer
                        section={section}
                        onChange={(content) => updateSection(index, content)}
                        isSelected={isSel}
                      />

                      {/* Inline settings shown below the selected section (no right panel) */}
                      {isSel && (
                        <div className="border-t border-gray-200 p-4 bg-gray-50" onClick={(e) => e.stopPropagation()}>
                          <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-3">{sectionLabel(section.type)} settings</div>
                          <PropertiesPanel
                            section={section}
                            sectionIndex={index}
                            onChange={(content) => updateSection(index, content)}
                            onOpenPhotos={openPhotoBrowser}
                          />
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile: Add section FAB (palette hidden on small screens) */}
      <button
        onClick={() => setShowMobileSidebar(true)}
        className="md:hidden fixed right-4 z-[100015] inline-flex items-center gap-1.5 px-4 py-3 rounded-full bg-blue-600 text-white text-[14px] font-semibold shadow-lg active:scale-95 transition-transform"
        style={{ bottom: 'calc(env(safe-area-inset-bottom) + 1rem)' }}
      >
        <Plus className="h-5 w-5" /> Add section
      </button>

      {/* Mobile: Add section bottom sheet (docs-builder style, grouped) */}
      {showMobileSidebar && (
        <div
          className="md:hidden fixed inset-0 z-[100020] bg-black/40 flex items-end"
          onClick={() => setShowMobileSidebar(false)}
        >
          <div
            className="bg-white rounded-t-2xl w-full max-h-[75vh] overflow-y-auto p-4"
            style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 1rem)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-[15px] font-semibold text-gray-900">Add a section</h3>
              <button onClick={() => setShowMobileSidebar(false)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500">
                <X className="h-5 w-5" />
              </button>
            </div>
            {sectionGroups.map((group) => (
              <div key={group.label} className="mb-4">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1.5 px-1">{group.label}</div>
                <div className="grid grid-cols-2 gap-2">
                  {group.types.map((type) => {
                    const Icon = sectionIcon(type);
                    return (
                      <button
                        key={type}
                        onClick={() => { addSection(type); setShowMobileSidebar(false); }}
                        className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-[13px] text-gray-700 bg-gray-50 hover:bg-blue-50 hover:text-blue-700 transition-colors text-left"
                      >
                        <Icon className="h-4 w-4 flex-shrink-0" /> {sectionLabel(type)}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Photo Browser Modal (My Photos + Media Library + Stock Photos) */}
      <StockPhotoBrowser
        isOpen={showStockPhotos}
        onClose={() => {
          setShowStockPhotos(false);
          setStockPhotoTarget(null);
        }}
        onSelectImage={handlePhotoSelect}
        uploadedImages={uploadedImages}
        onDeleteImage={handleDeleteUploadedImage}
        onUploadImages={handleImageUpload}
        isUploading={isUploadingImages}
        businessId={selectedBusiness?.id}
      />

      {showUnsavedDialog && (
        <div className="fixed inset-0 z-[100020] flex items-center justify-center bg-black/40" onClick={() => setShowUnsavedDialog(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-[90%] max-w-sm mx-4 overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 text-center">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Save className="h-6 w-6 text-orange-500" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Unsaved Changes</h3>
              <p className="text-sm text-gray-500">You have unsaved changes that will be lost if you close the editor.</p>
            </div>
            <div className="border-t border-gray-100 flex">
              <button
                onClick={() => {
                  setShowUnsavedDialog(false);
                  onClose();
                }}
                className="flex-1 py-3.5 text-sm font-semibold text-red-500 hover:bg-red-50 active:bg-red-100 transition-colors touch-manipulation"
              >
                Discard
              </button>
              <div className="w-px bg-gray-100" />
              <button
                onClick={() => {
                  setShowUnsavedDialog(false);
                  handleSave();
                }}
                className="flex-1 py-3.5 text-sm font-semibold text-blue-600 hover:bg-blue-50 active:bg-blue-100 transition-colors touch-manipulation"
              >
                Save & Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
}

// Section Renderer Component
// Toolbar dropdown for font size
function ToolbarFontSize({ onFormat }: { onFormat: (cmd: string, val?: string) => void }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        className={`p-2 rounded-lg touch-manipulation ${open ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:bg-gray-100 active:bg-blue-100'}`}
        title="Font Size"
        onMouseDown={(e) => e.preventDefault()}
        onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
      >
        <span className="text-xs font-bold w-4 h-4 flex items-center justify-center">A</span>
      </button>
      {open && (
        <div
          className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg p-1 z-30 min-w-[120px]"
          onMouseDown={(e) => e.preventDefault()}
        >
          {[
            { label: 'Small', size: '1' },
            { label: 'Normal', size: '3' },
            { label: 'Medium', size: '4' },
            { label: 'Large', size: '5' },
            { label: 'X-Large', size: '7' },
          ].map((s) => (
            <button
              key={s.size}
              className="w-full text-left px-3 py-2 text-sm rounded-lg hover:bg-gray-100 active:bg-blue-100 touch-manipulation"
              onClick={(e) => { e.stopPropagation(); onFormat('fontSize', s.size); setOpen(false); }}
            >
              {s.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Toolbar color picker - uses native color input for consistency with properties panel
function ToolbarColorPicker({ onFormat }: { onFormat: (cmd: string, val?: string) => void }) {
  const colorRef = useRef<HTMLInputElement>(null);
  return (
    <div className="relative">
      <button
        className="p-2.5 rounded-lg text-gray-600 hover:bg-gray-100 active:bg-blue-100 touch-manipulation"
        title="Text Color"
        onMouseDown={(e) => e.preventDefault()}
        onClick={(e) => { e.stopPropagation(); colorRef.current?.click(); }}
      >
        <Palette className="h-4 w-4" />
      </button>
      <input
        ref={colorRef}
        type="color"
        className="absolute w-0 h-0 opacity-0 pointer-events-none"
        onMouseDown={(e) => e.stopPropagation()}
        onChange={(e) => onFormat('foreColor', e.target.value)}
      />
    </div>
  );
}

// Text formatting toolbar rendered between header and content area
function TextFormattingToolbar() {
  const fmtCmd = (cmd: string, val?: string) => {
    document.execCommand(cmd, false, val);
  };

  const btnClass = "p-2.5 rounded-lg text-gray-600 hover:bg-gray-100 active:bg-blue-100 touch-manipulation";

  return (
    <div
      className="bg-white border-b border-gray-200 px-2 sm:px-4 py-1.5 flex flex-wrap items-center gap-0.5 flex-shrink-0"
      onMouseDown={(e) => e.preventDefault()}
      onPointerDown={(e) => e.preventDefault()}
      onClick={(e) => e.stopPropagation()}
    >
      <button className={btnClass} title="Bold" onClick={() => fmtCmd('bold')}>
        <Bold className="h-4 w-4" />
      </button>
      <button className={btnClass} title="Italic" onClick={() => fmtCmd('italic')}>
        <Italic className="h-4 w-4" />
      </button>
      <button className={btnClass} title="Underline" onClick={() => fmtCmd('underline')}>
        <Underline className="h-4 w-4" />
      </button>
      <button className={btnClass} title="Strikethrough" onClick={() => fmtCmd('strikeThrough')}>
        <Type className="h-4 w-4" style={{ textDecoration: 'line-through' }} />
      </button>

      <div className="w-px h-5 bg-gray-200 mx-1" />

      <ToolbarFontSize onFormat={fmtCmd} />
      <ToolbarColorPicker onFormat={fmtCmd} />

      <div className="w-px h-5 bg-gray-200 mx-1" />

      <button className={btnClass} title="Align Left" onClick={() => fmtCmd('justifyLeft')}>
        <AlignLeft className="h-4 w-4" />
      </button>
      <button className={btnClass} title="Align Center" onClick={() => fmtCmd('justifyCenter')}>
        <AlignCenter className="h-4 w-4" />
      </button>
      <button className={btnClass} title="Align Right" onClick={() => fmtCmd('justifyRight')}>
        <AlignRight className="h-4 w-4" />
      </button>

      <div className="w-px h-5 bg-gray-200 mx-1" />

      <button className={btnClass} title="Bullet List" onClick={() => fmtCmd('insertUnorderedList')}>
        <List className="h-4 w-4" />
      </button>
      <button className={btnClass} title="Numbered List" onClick={() => fmtCmd('insertOrderedList')}>
        <ListOrdered className="h-4 w-4" />
      </button>

      <div className="w-px h-5 bg-gray-200 mx-1" />

      <button className={btnClass} title="Clear Formatting" onClick={() => fmtCmd('removeFormat')}>
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

// Inline editable text component
function InlineEdit({ value, onChange, placeholder, style, className, multiline, tag, richText = true, autoFocus = false }: {
  value: string;
  onChange: (val: string) => void;
  placeholder?: string;
  style?: React.CSSProperties;
  className?: string;
  multiline?: boolean;
  tag?: string;
  richText?: boolean;
  autoFocus?: boolean;
}) {
  const ref = useRef<HTMLElement>(null);
  const lastSaved = useRef(value || '');

  useEffect(() => {
    if (ref.current && autoFocus) {
      requestAnimationFrame(() => ref.current?.focus());
    }
  }, [autoFocus]);

  useEffect(() => {
    if (!ref.current) return;
    if (document.activeElement === ref.current) return;
    const cur = richText ? ref.current.innerHTML : ref.current.innerText;
    const target = value || '';
    if (cur !== target) {
      if (richText) ref.current.innerHTML = target;
      else ref.current.innerText = target;
      lastSaved.current = target;
    }
  }, [value, richText]);

  const save = useCallback(() => {
    if (!ref.current) return;
    const newVal = richText ? ref.current.innerHTML : ref.current.innerText;
    if (newVal !== lastSaved.current) {
      lastSaved.current = newVal;
      onChange(newVal);
    }
  }, [onChange, richText]);

  const Tag = (tag || 'div') as any;

  return (
    <Tag
      ref={ref}
      contentEditable
      suppressContentEditableWarning
      onBlur={() => save()}
      onInput={() => save()}
      onMouseUp={() => {
        setTimeout(() => {
          const sel = window.getSelection();
          if (sel && sel.rangeCount > 0 && !sel.isCollapsed && sel.toString().trim().length > 0) {
            document.dispatchEvent(new CustomEvent('ce-text-selected', {
              detail: { element: ref.current }
            }));
          }
        }, 10);
      }}
      onKeyDown={(e: React.KeyboardEvent) => {
        e.stopPropagation();
        if (!multiline && e.key === 'Enter') { e.preventDefault(); ref.current?.blur(); }
      }}
      onClick={(e: React.MouseEvent) => e.stopPropagation()}
      onFocus={(e: React.FocusEvent) => e.stopPropagation()}
      style={{
        ...style,
        outline: 'none',
        cursor: 'text',
        minHeight: '1em',
        borderRadius: '4px',
      }}
      className={`${className || ''} ${!value ? 'empty-editable' : ''}`}
      data-placeholder={placeholder}
    />
  );
}

function SectionRenderer({ section, onChange, isSelected }: any) {
  const { selectedBusiness } = useBusiness();
  const { type, content } = section;
  const logoUrl = selectedBusiness?.booking_settings?.logo_url;

  const editHint = isSelected ? { boxShadow: 'inset 0 0 0 1px rgba(59,130,246,0.2)' } : {};

  switch (type) {
    case 'header': {
      const headerDisplayMode = content.displayMode || (content.showBusinessName !== false ? 'name' : 'name');
      const headerLogoSize = content.logoSize || 80;
      const headerLogoFilter = content.logoColorMode === 'white'
        ? 'brightness(0) invert(1)'
        : content.logoColorMode === 'black'
          ? 'brightness(0)'
          : 'none';
      return (
        <div
          style={{ 
            backgroundColor: content.backgroundColor, 
            padding: content.padding,
            color: content.textColor || '#ffffff'
          }}
          className="text-center"
        >
          {headerDisplayMode === 'logo' ? (
            logoUrl ? (
              <img 
                src={logoUrl} 
                alt={selectedBusiness?.name || 'Business Logo'} 
                className="mx-auto object-contain"
                style={{ 
                  height: `${headerLogoSize}px`,
                  filter: headerLogoFilter
                }}
              />
            ) : (
              <div 
                className="font-bold text-2xl uppercase tracking-wider"
                style={{ color: content.textColor || '#ffffff' }}
              >
                {selectedBusiness?.name || 'Your Business Name'}
              </div>
            )
          ) : (
            <div 
              className="font-bold text-2xl uppercase tracking-wider"
              style={{ color: content.textColor || '#ffffff' }}
            >
              {selectedBusiness?.name || 'Your Business Name'}
            </div>
          )}
        </div>
      );
    }

    case 'hero':
      return (
        <div style={{ backgroundColor: content.backgroundColor }} className="p-8 text-center">
          {content.image && (
            <img src={content.image} alt="Hero" className="w-full mb-4 rounded-lg" />
          )}
          {isSelected ? (
            <>
              <InlineEdit
                value={content.heading || ''}
                onChange={(val) => onChange({ heading: val })}
                placeholder="Enter heading..."
                tag="h1"
                autoFocus
                className="text-3xl font-bold mb-2"
                style={{ ...editHint }}
              />
              <InlineEdit
                value={content.subheading || ''}
                onChange={(val) => onChange({ subheading: val })}
                placeholder="Enter subheading..."
                tag="p"
                className="text-lg text-gray-600 mb-4"
                style={{ ...editHint }}
              />
              <div className="inline-block" style={editHint}>
                <InlineEdit
                  value={content.buttonText || ''}
                  onChange={(val) => onChange({ buttonText: val })}
                  placeholder="Button text..."
                  className="px-6 py-3 rounded-lg font-medium inline-block"
                  style={{
                    ...editHint,
                    backgroundColor: content.buttonBackgroundColor || '#6366f1',
                    color: content.buttonColor || '#ffffff',
                  }}
                />
              </div>
            </>
          ) : (
            <>
              {content.heading
                ? <h1 className="text-3xl font-bold mb-2" dangerouslySetInnerHTML={{ __html: content.heading }} />
                : <h1 className="text-3xl font-bold mb-2"><span className="opacity-40">Enter heading...</span></h1>}
              {content.subheading
                ? <p className="text-lg text-gray-600 mb-4" dangerouslySetInnerHTML={{ __html: content.subheading }} />
                : <p className="text-lg text-gray-600 mb-4"><span className="opacity-40">Enter subheading...</span></p>}
              {content.buttonText
                ? (
                  <button
                    className="px-6 py-3 rounded-lg font-medium"
                    style={{
                      backgroundColor: content.buttonBackgroundColor || '#6366f1',
                      color: content.buttonColor || '#ffffff',
                    }}
                    dangerouslySetInnerHTML={{ __html: content.buttonText }}
                  />
                )
                : null}
            </>
          )}
        </div>
      );

    case 'text':
      return (
        <div className="p-6" style={{ textAlign: content.align }}>
          {isSelected ? (
            <InlineEdit
              value={content.text || ''}
              onChange={(val) => onChange({ text: val })}
              placeholder="Type your text here..."
              multiline
              autoFocus
              style={{
                fontSize: content.fontSize,
                color: content.color,
                whiteSpace: 'pre-wrap',
                lineHeight: '1.6',
                ...editHint,
              }}
            />
          ) : (
            content.text
              ? <div style={{ fontSize: content.fontSize, color: content.color, whiteSpace: 'pre-wrap', lineHeight: '1.6' }} dangerouslySetInnerHTML={{ __html: content.text }} />
              : <div style={{ fontSize: content.fontSize, color: content.color, lineHeight: '1.6' }}><span className="opacity-40">Type your text here...</span></div>
          )}
        </div>
      );

    case 'button':
      return (
        <div className="p-6" style={{ textAlign: content.align }}>
          {isSelected ? (
            <div className="inline-block" style={editHint}>
              <InlineEdit
                value={content.text || ''}
                onChange={(val) => onChange({ text: val })}
                placeholder="Button text..."
                autoFocus
                className="px-6 py-3 rounded-lg font-medium inline-block"
                style={{ backgroundColor: content.backgroundColor, color: content.color }}
              />
            </div>
          ) : (
            content.text
              ? <button style={{ backgroundColor: content.backgroundColor, color: content.color }} className="px-6 py-3 rounded-lg font-medium" dangerouslySetInnerHTML={{ __html: content.text }} />
              : <button style={{ backgroundColor: content.backgroundColor, color: content.color }} className="px-6 py-3 rounded-lg font-medium">Button</button>
          )}
        </div>
      );

    case 'divider':
      return (
        <div style={{ padding: content.margin }}>
          <hr style={{ borderColor: content.color, borderWidth: content.thickness }} />
        </div>
      );

    case 'spacer':
      return (
        <div style={{ height: content?.height || 20, backgroundColor: 'transparent' }} className={isSelected ? 'border border-dashed border-blue-300 rounded' : ''}>
          {isSelected && (
            <div className="flex items-center justify-center h-full text-xs text-blue-400">
              {content?.height || 20}px spacer
            </div>
          )}
        </div>
      );

    case 'card':
      return (
        <div className="p-6">
          <div
            style={{ backgroundColor: content.backgroundColor, borderColor: content.borderColor }}
            className="p-4 rounded-lg border"
          >
            {isSelected ? (
              <>
                <InlineEdit
                  value={content.title || ''}
                  onChange={(val) => onChange({ title: val })}
                  placeholder="Card title..."
                  tag="h3"
                  autoFocus
                  className="font-semibold mb-2"
                  style={editHint}
                />
                <InlineEdit
                  value={content.content || ''}
                  onChange={(val) => onChange({ content: val })}
                  placeholder="Card content..."
                  tag="p"
                  className="text-gray-600"
                  multiline
                  style={editHint}
                />
              </>
            ) : (
              <>
                {content.title
                  ? <h3 className="font-semibold mb-2" dangerouslySetInnerHTML={{ __html: content.title }} />
                  : <h3 className="font-semibold mb-2"><span className="opacity-40">Card title...</span></h3>}
                {content.content
                  ? <p className="text-gray-600" dangerouslySetInnerHTML={{ __html: content.content }} />
                  : <p className="text-gray-600"><span className="opacity-40">Card content...</span></p>}
              </>
            )}
          </div>
        </div>
      );

    case 'image':
      return (
        <div className="p-6" style={{ textAlign: content.align || 'center' }}>
          {content.url ? (
            <img
              src={content.url}
              alt={content.alt || 'Image'}
              style={{ width: content.width || '100%' }}
              className="mx-auto rounded-lg"
            />
          ) : (
            <div className="bg-gray-100 rounded-lg p-12 text-center">
              <ImageIcon className="h-12 w-12 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-500">Click to add an image</p>
            </div>
          )}
        </div>
      );

    case 'footer':
      return (
        <div
          style={{ backgroundColor: content.backgroundColor, color: content.color }}
          className="p-6 text-center text-sm"
        >
          {isSelected ? (
            <InlineEdit
              value={content.text || ''}
              onChange={(val) => onChange({ text: val })}
              placeholder="Footer text..."
              multiline
              autoFocus
              style={{ color: content.color, ...editHint }}
            />
          ) : (
            content.text
              ? <span dangerouslySetInnerHTML={{ __html: content.text }} />
              : <span className="opacity-40">Footer text...</span>
          )}
        </div>
      );

    case 'html':
      return (
        <div className="p-6">
          {isSelected ? (
            <InlineEdit
              value={typeof content === 'string' ? content : (content?.html || '')}
              onChange={(val) => onChange(typeof content === 'string' ? val : { html: val })}
              placeholder="Enter HTML content..."
              multiline
              richText={false}
              className="font-mono text-sm bg-gray-50 p-3 rounded border border-gray-200"
              style={editHint}
            />
          ) : (
            <div className="font-mono text-sm bg-gray-50 p-3 rounded border border-gray-200">
              {(typeof content === 'string' ? content : content?.html) || <span className="opacity-40">Enter HTML content...</span>}
            </div>
          )}
        </div>
      );

    default:
      return null;
  }
}

// Processes a logo image through Canvas to produce a solid-color version, uploads to storage, and returns the public URL.
// Fetches as blob first to avoid canvas CORS taint when reading pixel data from cross-origin images.
async function processLogoColor(originalUrl: string, colorMode: 'white' | 'black', businessId: string): Promise<string | null> {
  try {
    console.log('[processLogoColor] Starting:', { originalUrl, colorMode, businessId });

    // Try multiple approaches to fetch the image as a blob
    let imageBlob: Blob | null = null;

    // Approach 1: Direct fetch (works for same-origin and CORS-enabled URLs)
    try {
      const response = await fetch(originalUrl, { mode: 'cors' });
      if (response.ok) {
        imageBlob = await response.blob();
        console.log('[processLogoColor] Fetched via direct fetch, size:', imageBlob.size);
      }
    } catch (fetchErr) {
      console.warn('[processLogoColor] Direct fetch failed, trying no-cors proxy:', fetchErr);
    }

    // Approach 2: Fetch with crossOrigin via an img element
    if (!imageBlob) {
      const proxyImg = document.createElement('img');
      proxyImg.crossOrigin = 'anonymous';
      await new Promise<void>((resolve, reject) => {
        proxyImg.onload = () => resolve();
        proxyImg.onerror = () => reject(new Error('Proxy image load failed'));
        proxyImg.src = originalUrl + (originalUrl.includes('?') ? '&' : '?') + 'cb=' + Date.now();
      });
      const tmpCanvas = document.createElement('canvas');
      tmpCanvas.width = proxyImg.naturalWidth;
      tmpCanvas.height = proxyImg.naturalHeight;
      const tmpCtx = tmpCanvas.getContext('2d')!;
      tmpCtx.drawImage(proxyImg, 0, 0);
      imageBlob = await new Promise<Blob | null>((resolve) => tmpCanvas.toBlob(resolve, 'image/png'));
      console.log('[processLogoColor] Fetched via proxy canvas, size:', imageBlob?.size);
    }

    if (!imageBlob || imageBlob.size === 0) {
      throw new Error('Could not fetch logo image');
    }

    const objectUrl = URL.createObjectURL(imageBlob);
    const img = document.createElement('img');
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('Failed to load logo from blob URL'));
      img.src = objectUrl;
    });

    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext('2d')!;
    ctx.drawImage(img, 0, 0);
    URL.revokeObjectURL(objectUrl);

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;
    const targetValue = colorMode === 'white' ? 255 : 0;

    for (let i = 0; i < data.length; i += 4) {
      data[i] = targetValue;
      data[i + 1] = targetValue;
      data[i + 2] = targetValue;
    }
    ctx.putImageData(imageData, 0, 0);

    const processedBlob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
    if (!processedBlob) throw new Error('Canvas toBlob returned null');

    console.log('[processLogoColor] Processed blob size:', processedBlob.size);

    const fileName = `logo_${colorMode}_${businessId}_${Date.now()}.png`;
    const { error: uploadError } = await supabase.storage
      .from('avatars')
      .upload(fileName, processedBlob, { contentType: 'image/png', upsert: true });

    if (uploadError) {
      console.error('[processLogoColor] Upload error:', uploadError);
      throw new Error(`Upload failed: ${uploadError.message}`);
    }

    const { data: { publicUrl } } = supabase.storage
      .from('avatars')
      .getPublicUrl(fileName);

    console.log('[processLogoColor] Success! URL:', publicUrl);
    return publicUrl;
  } catch (err) {
    console.error('[processLogoColor] FAILED:', err);
    return null;
  }
}

// Properties Panel Component — design-only controls (text is edited inline on the preview)
function PropertiesPanel({ section, sectionIndex, onChange, onOpenPhotos }: any) {
  const { selectedBusiness } = useBusiness();
  const [isProcessingLogo, setIsProcessingLogo] = useState(false);
  const { type, content } = section;
  const logoUrl = selectedBusiness?.booking_settings?.logo_url;

  const handleLogoColorChange = async (colorMode: string) => {
    if (colorMode === 'original') {
      onChange({ logoColorMode: 'original', processedLogoUrl: null });
      return;
    }
    if (!logoUrl || !selectedBusiness?.id) return;

    setIsProcessingLogo(true);
    try {
      const processedUrl = await processLogoColor(logoUrl, colorMode as 'white' | 'black', selectedBusiness.id);
      if (processedUrl) {
        onChange({ logoColorMode: colorMode, processedLogoUrl: processedUrl });
      } else {
        alert('Failed to process logo color. Please try again.');
        onChange({ logoColorMode: 'original', processedLogoUrl: null });
      }
    } catch (err) {
      console.error('Logo color processing error:', err);
      alert('Failed to process logo color. Please try again.');
      onChange({ logoColorMode: 'original', processedLogoUrl: null });
    } finally {
      setIsProcessingLogo(false);
    }
  };

  const inputClass = "w-full px-3.5 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 touch-manipulation";
  const inputStyle = { fontSize: '16px', minHeight: '44px' };
  const labelClass = "block text-sm font-medium text-gray-700 mb-1.5";
  const colorInputClass = "w-full h-12 border border-gray-300 rounded-xl touch-manipulation";
  const toggleBtn = (active: boolean) => `flex-1 px-3.5 py-2.5 rounded-xl border font-medium touch-manipulation text-sm ${active ? 'bg-blue-100 border-blue-300 text-blue-700' : 'bg-gray-100 border-gray-300 text-gray-700'}`;
  const iconBtn = (active: boolean) => `p-3 rounded-xl touch-manipulation ${active ? 'bg-blue-100 border border-blue-300' : 'bg-gray-100 border border-gray-200'}`;

  const sectionLabel = type.charAt(0).toUpperCase() + type.slice(1);

  switch (type) {
    case 'header': {
      const currentDisplayMode = content.displayMode || 'name';
      const currentLogoColorMode = content.logoColorMode || 'original';
      const currentLogoSize = content.logoSize || 80;
      return (
        <div className="space-y-4">
          <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold">Header Design</p>
          <div>
            <label className={labelClass}>Display</label>
            <div className="flex space-x-2">
              <button onClick={() => onChange({ displayMode: 'name' })} className={toggleBtn(currentDisplayMode === 'name')} style={{ minHeight: '44px' }}>Business Name</button>
              <button onClick={() => onChange({ displayMode: 'logo' })} className={toggleBtn(currentDisplayMode === 'logo')} style={{ minHeight: '44px' }}>Business Logo</button>
            </div>
          </div>

          {currentDisplayMode === 'logo' && (
            <>
              <div>
                <label className={labelClass}>Logo Color</label>
                <div className="flex space-x-2">
                  {[
                    { value: 'original', label: 'Original' },
                    { value: 'white', label: 'White' },
                    { value: 'black', label: 'Black' },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => handleLogoColorChange(opt.value)}
                      disabled={isProcessingLogo}
                      className={`${toggleBtn(currentLogoColorMode === opt.value)} ${isProcessingLogo ? 'opacity-50' : ''}`}
                      style={{ minHeight: '44px' }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                {isProcessingLogo && <p className="text-xs text-blue-600 mt-1.5">Processing logo...</p>}
              </div>
              <div>
                <label className={labelClass}>Logo Size</label>
                <div className="flex items-center space-x-3">
                  <input type="range" min={30} max={200} step={5} value={currentLogoSize} onChange={(e) => onChange({ logoSize: parseInt(e.target.value) })} className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600 touch-manipulation" style={{ minHeight: '44px' }} />
                  <span className="text-sm font-medium text-gray-600 w-12 text-right">{currentLogoSize}px</span>
                </div>
              </div>
              {logoUrl && (
                <div>
                  <label className={labelClass}>Preview</label>
                  <div className="flex items-center justify-center rounded-xl border border-gray-200 p-4" style={{ backgroundColor: content.backgroundColor || '#6366f1' }}>
                    <img src={logoUrl} alt="Logo Preview" className="object-contain" style={{ height: `${currentLogoSize}px`, filter: currentLogoColorMode === 'white' ? 'brightness(0) invert(1)' : currentLogoColorMode === 'black' ? 'brightness(0)' : 'none' }} />
                  </div>
                </div>
              )}
              {!logoUrl && (
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-xl text-sm text-yellow-700">
                  No logo uploaded. Business name will be shown instead. Upload a logo in your business settings.
                </div>
              )}
            </>
          )}

          {currentDisplayMode === 'name' && (
            <div>
              <label className={labelClass}>Text Color</label>
              <input type="color" value={content.textColor || '#ffffff'} onChange={(e) => onChange({ textColor: e.target.value })} className={colorInputClass} />
            </div>
          )}

          <div>
            <label className={labelClass}>Background Color</label>
            <input type="color" value={content.backgroundColor} onChange={(e) => onChange({ backgroundColor: e.target.value })} className={colorInputClass} />
          </div>
          <div>
            <label className={labelClass}>Padding</label>
            <select value={content.padding} onChange={(e) => onChange({ padding: e.target.value })} className={inputClass} style={inputStyle}>
              <option value="20px">Small</option>
              <option value="30px">Medium</option>
              <option value="40px">Large</option>
              <option value="50px">Extra Large</option>
            </select>
          </div>
        </div>
      );
    }

    case 'text':
      return (
        <div className="space-y-4">
          <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold">Text Block</p>
          <div className="text-sm text-gray-500 bg-blue-50 rounded-xl p-3 flex items-center gap-2">
            <Type className="h-4 w-4 text-blue-500 flex-shrink-0" />
            <span>Click on the text to edit. Use the floating toolbar for bold, italic, size, color, and alignment.</span>
          </div>
        </div>
      );

    case 'hero':
      return (
        <div className="space-y-4">
          <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold">Hero Design</p>
          <div className="text-sm text-gray-500 bg-blue-50 rounded-xl p-3 flex items-center gap-2">
            <Type className="h-4 w-4 text-blue-500 flex-shrink-0" />
            <span>Click on the heading, subheading, or button to edit directly.</span>
          </div>
          <div>
            <label className={labelClass}>Background Color</label>
            <input type="color" value={content.backgroundColor || '#f3f4f6'} onChange={(e) => onChange({ backgroundColor: e.target.value })} className={colorInputClass} />
          </div>
          <div>
            <label className={labelClass}>Image</label>
            <div className="space-y-2">
              <input type="text" value={content.image || ''} onChange={(e) => onChange({ image: e.target.value })} placeholder="Enter image URL..." className={inputClass} style={inputStyle} />
              <button onClick={() => onOpenPhotos(sectionIndex, 'image')} className="w-full px-3.5 py-2.5 bg-blue-100 text-blue-700 rounded-xl hover:bg-blue-200 active:bg-blue-200 transition-colors flex items-center justify-center space-x-2 font-medium touch-manipulation" style={{ minHeight: '44px' }}>
                <Images className="h-4 w-4" /><span>Browse Photos</span>
              </button>
              {content.image && (
                <div className="mt-2 p-2 bg-gray-50 rounded-xl">
                  <img src={content.image} alt="Preview" className="w-full h-24 object-cover rounded-lg" />
                </div>
              )}
            </div>
          </div>
          <div>
            <label className={labelClass}>Button Color</label>
            <input
              type="color"
              value={content.buttonBackgroundColor || '#6366f1'}
              onChange={(e) => onChange({ buttonBackgroundColor: e.target.value })}
              className={colorInputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Button Text Color</label>
            <input
              type="color"
              value={content.buttonColor || '#ffffff'}
              onChange={(e) => onChange({ buttonColor: e.target.value })}
              className={colorInputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Button URL</label>
            <input type="text" value={content.buttonUrl || ''} onChange={(e) => onChange({ buttonUrl: e.target.value })} placeholder="https://..." className={inputClass} style={inputStyle} />
          </div>
        </div>
      );

    case 'button':
      return (
        <div className="space-y-4">
          <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold">Button Design</p>
          <div className="text-sm text-gray-500 bg-blue-50 rounded-xl p-3 flex items-center gap-2">
            <Type className="h-4 w-4 text-blue-500 flex-shrink-0" />
            <span>Click button to edit text directly.</span>
          </div>
          <div>
            <label className={labelClass}>Button URL</label>
            <input type="text" value={content.url || ''} onChange={(e) => onChange({ url: e.target.value })} placeholder="https://..." className={inputClass} style={inputStyle} />
          </div>
          <div>
            <label className={labelClass}>Alignment</label>
            <div className="flex space-x-2">
              <button onClick={() => onChange({ align: 'left' })} className={iconBtn(content.align === 'left')} style={{ minWidth: '44px', minHeight: '44px' }}><AlignLeft className="h-4 w-4" /></button>
              <button onClick={() => onChange({ align: 'center' })} className={iconBtn((content.align || 'center') === 'center')} style={{ minWidth: '44px', minHeight: '44px' }}><AlignCenter className="h-4 w-4" /></button>
              <button onClick={() => onChange({ align: 'right' })} className={iconBtn(content.align === 'right')} style={{ minWidth: '44px', minHeight: '44px' }}><AlignRight className="h-4 w-4" /></button>
            </div>
          </div>
          <div>
            <label className={labelClass}>Background Color</label>
            <input type="color" value={content.backgroundColor || '#6366f1'} onChange={(e) => onChange({ backgroundColor: e.target.value })} className={colorInputClass} />
          </div>
        </div>
      );

    case 'image':
      return (
        <div className="space-y-4">
          <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold">Image Settings</p>
          <div>
            <label className={labelClass}>Image</label>
            <div className="space-y-2">
              <input type="text" value={content.url || ''} onChange={(e) => onChange({ url: e.target.value })} placeholder="Enter image URL..." className={inputClass} style={inputStyle} />
              <button onClick={() => onOpenPhotos(sectionIndex, 'url')} className="w-full px-3.5 py-2.5 bg-blue-100 text-blue-700 rounded-xl hover:bg-blue-200 active:bg-blue-200 transition-colors flex items-center justify-center space-x-2 font-medium touch-manipulation" style={{ minHeight: '44px' }}>
                <Images className="h-4 w-4" /><span>Browse Photos</span>
              </button>
              {content.url && (
                <div className="mt-2 p-2 bg-gray-50 rounded-xl"><img src={content.url} alt="Preview" className="w-full h-24 object-cover rounded-lg" /></div>
              )}
            </div>
          </div>
          <div>
            <label className={labelClass}>Alt Text</label>
            <input type="text" value={content.alt || ''} onChange={(e) => onChange({ alt: e.target.value })} placeholder="Describe the image..." className={inputClass} style={inputStyle} />
          </div>
          <div>
            <label className={labelClass}>Width</label>
            <input type="text" value={content.width || '100%'} onChange={(e) => onChange({ width: e.target.value })} className={inputClass} style={inputStyle} />
          </div>
          <div>
            <label className={labelClass}>Alignment</label>
            <div className="flex space-x-2">
              <button onClick={() => onChange({ align: 'left' })} className={iconBtn(content.align === 'left')} style={{ minWidth: '44px', minHeight: '44px' }}><AlignLeft className="h-4 w-4" /></button>
              <button onClick={() => onChange({ align: 'center' })} className={iconBtn((content.align || 'center') === 'center')} style={{ minWidth: '44px', minHeight: '44px' }}><AlignCenter className="h-4 w-4" /></button>
              <button onClick={() => onChange({ align: 'right' })} className={iconBtn(content.align === 'right')} style={{ minWidth: '44px', minHeight: '44px' }}><AlignRight className="h-4 w-4" /></button>
            </div>
          </div>
        </div>
      );

    case 'divider':
      return (
        <div className="space-y-4">
          <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold">Divider Design</p>
          <div>
            <label className={labelClass}>Color</label>
            <input type="color" value={content.color || '#e5e7eb'} onChange={(e) => onChange({ color: e.target.value })} className={colorInputClass} />
          </div>
          <div>
            <label className={labelClass}>Thickness</label>
            <select value={content.thickness || '1px'} onChange={(e) => onChange({ thickness: e.target.value })} className={inputClass} style={inputStyle}>
              <option value="1px">Thin</option>
              <option value="2px">Medium</option>
              <option value="3px">Thick</option>
              <option value="4px">Extra Thick</option>
            </select>
          </div>
          <div>
            <label className={labelClass}>Margin</label>
            <select value={content.margin || '10px 20px'} onChange={(e) => onChange({ margin: e.target.value })} className={inputClass} style={inputStyle}>
              <option value="5px 20px">Small</option>
              <option value="10px 20px">Medium</option>
              <option value="20px 20px">Large</option>
              <option value="30px 20px">Extra Large</option>
            </select>
          </div>
        </div>
      );

    case 'spacer':
      return (
        <div className="space-y-4">
          <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold">Spacer Settings</p>
          <div>
            <label className={labelClass}>Height</label>
            <div className="flex items-center space-x-3">
              <input type="range" min={10} max={100} step={5} value={content.height || 20} onChange={(e) => onChange({ height: parseInt(e.target.value) })} className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600 touch-manipulation" style={{ minHeight: '44px' }} />
              <span className="text-sm font-medium text-gray-600 w-12 text-right">{content.height || 20}px</span>
            </div>
          </div>
        </div>
      );

    case 'card':
      return (
        <div className="space-y-4">
          <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold">Card Design</p>
          <div className="text-sm text-gray-500 bg-blue-50 rounded-xl p-3 flex items-center gap-2">
            <Type className="h-4 w-4 text-blue-500 flex-shrink-0" />
            <span>Click on the card to edit title and content directly.</span>
          </div>
          <div>
            <label className={labelClass}>Background Color</label>
            <input type="color" value={content.backgroundColor || '#f9fafb'} onChange={(e) => onChange({ backgroundColor: e.target.value })} className={colorInputClass} />
          </div>
          <div>
            <label className={labelClass}>Border Color</label>
            <input type="color" value={content.borderColor || '#e5e7eb'} onChange={(e) => onChange({ borderColor: e.target.value })} className={colorInputClass} />
          </div>
        </div>
      );

    case 'footer':
      return (
        <div className="space-y-4">
          <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold">Footer Design</p>
          <div className="text-sm text-gray-500 bg-blue-50 rounded-xl p-3 flex items-center gap-2">
            <Type className="h-4 w-4 text-blue-500 flex-shrink-0" />
            <span>Click on the footer to edit text directly.</span>
          </div>
          <div>
            <label className={labelClass}>Background Color</label>
            <input type="color" value={content.backgroundColor || '#f9fafb'} onChange={(e) => onChange({ backgroundColor: e.target.value })} className={colorInputClass} />
          </div>
        </div>
      );

    case 'html':
      return (
        <div className="space-y-4">
          <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold">HTML Block</p>
          <div className="text-sm text-gray-500 bg-blue-50 rounded-xl p-3 flex items-center gap-2">
            <Code className="h-4 w-4 text-blue-500 flex-shrink-0" />
            <span>Click on the block to edit raw HTML directly.</span>
          </div>
        </div>
      );

    default:
      return <div className="text-sm text-gray-500 p-2">Select a section to edit its properties</div>;
  }
}
